import { useState } from 'react';
import {
    Users, Plus, Shield, MessageSquare, ChevronRight, Eye,
    User, Briefcase, Search, X, UserPlus,
    CheckCircle, Tag, Settings
} from 'lucide-react';
import { useReplica } from '../contexts/ReplicaContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTranslation } from 'react-i18next';

interface CompanyReplicasViewProps {
    onNavigate: (view: string) => void;
}

interface TeamMember {
    id: string;
    type: 'replica' | 'human';
    name: string;
    email?: string;
    replicaId?: string;
}

export default function CompanyReplicasView({ onNavigate }: CompanyReplicasViewProps) {
    const { userReplicas } = useReplica();
    const { teams, currentWorkspace } = useWorkspace();
    const { t } = useTranslation('dashboard');

    // All replicas visible to this company (hired replicas + marketplace)
    const hiredReplicas = userReplicas;
    const [searchQuery, setSearchQuery] = useState('');
    const [showTeamBuilder, setShowTeamBuilder] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamDescription, setNewTeamDescription] = useState('');
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [humanEmail, setHumanEmail] = useState('');
    const [selectedTab, setSelectedTab] = useState<'replicas' | 'teams'>('replicas');

    const filteredReplicas = hiredReplicas.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TEAM BUILDER
    // ═══════════════════════════════════════════════════════════════════════════
    const addReplicaToTeam = (replicaId: string, replicaName: string) => {
        if (teamMembers.find(m => m.replicaId === replicaId)) return;
        setTeamMembers(prev => [...prev, {
            id: `replica-${replicaId}`,
            type: 'replica',
            name: replicaName,
            replicaId,
        }]);
    };

    const addHumanToTeam = () => {
        if (!humanEmail.trim() || teamMembers.find(m => m.email === humanEmail)) return;
        setTeamMembers(prev => [...prev, {
            id: `human-${Date.now()}`,
            type: 'human',
            name: humanEmail.split('@')[0],
            email: humanEmail,
        }]);
        setHumanEmail('');
    };

    const removeTeamMember = (memberId: string) => {
        setTeamMembers(prev => prev.filter(m => m.id !== memberId));
    };

    const handleCreateTeam = () => {
        if (!newTeamName.trim()) return;
        // In a real app, this would call an API
        console.log('Creating team:', { name: newTeamName, description: newTeamDescription, members: teamMembers });
        // Navigate to teams view after creation
        onNavigate('teams');
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // STATS BAR
    // ═══════════════════════════════════════════════════════════════════════════
    const StatsBar = () => (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
            <div className="card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-hig-accent/10 flex items-center justify-center">
                    <Briefcase size={20} className="text-hig-accent" />
                </div>
                <div>
                    <p className="text-2xl font-bold text-hig-text-primary">{hiredReplicas.length}</p>
                    <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Hired Replicas</p>
                </div>
            </div>
            <div className="card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle size={20} className="text-emerald-400" />
                </div>
                <div>
                    <p className="text-2xl font-bold text-hig-text-primary">
                        {hiredReplicas.filter(r => r.status === 'deployed').length}
                    </p>
                    <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Active</p>
                </div>
            </div>
            <div className="card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                    <Users size={20} className="text-indigo-400" />
                </div>
                <div>
                    <p className="text-2xl font-bold text-hig-text-primary">{teams.length}</p>
                    <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Teams</p>
                </div>
            </div>
            <div className="card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Shield size={20} className="text-amber-400" />
                </div>
                <div>
                    <p className="text-2xl font-bold text-hig-text-primary">100%</p>
                    <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Guardian Coverage</p>
                </div>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TEAM BUILDER PANEL
    // ═══════════════════════════════════════════════════════════════════════════
    const TeamBuilderPanel = () => (
        <div className="card overflow-hidden mb-8 border-2 border-indigo-500/20">
            <div className="p-6 border-b border-hig-divider bg-indigo-500/5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                            <Users size={20} className="text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-hig-text-primary">Create New Team</h3>
                            <p className="text-xs text-hig-text-secondary">Mix replicas and humans into collaborative teams</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowTeamBuilder(false)}
                        className="p-2 text-hig-text-tertiary hover:text-hig-text-primary rounded-lg hover:bg-hig-bg-tertiary transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            <div className="p-6 space-y-5">
                {/* Team Name & Description */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Team Name *</label>
                        <input
                            type="text"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            placeholder="e.g., Customer Support Alpha"
                            className="w-full bg-hig-bg-tertiary border border-hig-divider rounded-lg px-3 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Description</label>
                        <input
                            type="text"
                            value={newTeamDescription}
                            onChange={(e) => setNewTeamDescription(e.target.value)}
                            placeholder="Team purpose and scope"
                            className="w-full bg-hig-bg-tertiary border border-hig-divider rounded-lg px-3 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                        />
                    </div>
                </div>

                {/* Add Members */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Add Replicas */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Add Replicas</label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {hiredReplicas.length === 0 ? (
                                <p className="text-sm text-hig-text-tertiary p-3 bg-hig-bg-tertiary rounded-lg">
                                    No replicas available. <button onClick={() => onNavigate('marketplace')} className="text-hig-accent hover:underline">Hire some</button>
                                </p>
                            ) : (
                                hiredReplicas.map(replica => {
                                    const isAdded = teamMembers.find(m => m.replicaId === replica.id);
                                    return (
                                        <button
                                            key={replica.id}
                                            onClick={() => addReplicaToTeam(replica.id, replica.name)}
                                            disabled={!!isAdded}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isAdded
                                                ? 'bg-emerald-500/5 border border-emerald-500/20 cursor-default'
                                                : 'bg-hig-bg-tertiary hover:bg-hig-bg-elevated border border-hig-divider'
                                                }`}
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                                {replica.name.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-hig-text-primary truncate">{replica.name}</p>
                                                <p className="text-xs text-hig-text-tertiary">{replica.category}</p>
                                            </div>
                                            {isAdded ? (
                                                <CheckCircle size={16} className="text-emerald-400" />
                                            ) : (
                                                <Plus size={16} className="text-hig-text-tertiary" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Add Humans */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Add Human Members</label>
                        <div className="flex gap-2 mb-3">
                            <input
                                type="email"
                                value={humanEmail}
                                onChange={(e) => setHumanEmail(e.target.value)}
                                placeholder="colleague@company.com"
                                onKeyDown={(e) => e.key === 'Enter' && addHumanToTeam()}
                                className="flex-1 bg-hig-bg-tertiary border border-hig-divider rounded-lg px-3 py-2 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                            />
                            <button
                                onClick={addHumanToTeam}
                                disabled={!humanEmail.trim()}
                                className="px-3 py-2 rounded-lg bg-hig-accent/10 text-hig-accent border border-hig-accent/20 hover:bg-hig-accent/20 text-sm font-medium disabled:opacity-40 transition-colors"
                            >
                                <UserPlus size={16} />
                            </button>
                        </div>
                        <p className="text-xs text-hig-text-tertiary mb-2">Human members will receive an invite to join the team.</p>
                    </div>
                </div>

                {/* Current Team Members */}
                {teamMembers.length > 0 && (
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-2">
                            Team Members ({teamMembers.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {teamMembers.map(member => (
                                <div
                                    key={member.id}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${member.type === 'replica'
                                        ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                        : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                                        }`}
                                >
                                    {member.type === 'replica' ? (
                                        <User size={14} />
                                    ) : (
                                        <UserPlus size={14} />
                                    )}
                                    <span className="font-medium">{member.name}</span>
                                    <span className="text-[10px] uppercase opacity-60">{member.type}</span>
                                    <button
                                        onClick={() => removeTeamMember(member.id)}
                                        className="ml-1 p-0.5 hover:bg-white/10 rounded-full transition-colors"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Create Button */}
            <div className="p-6 border-t border-hig-divider bg-hig-bg-tertiary/30 flex items-center justify-end gap-3">
                <button
                    onClick={() => setShowTeamBuilder(false)}
                    className="px-5 py-2.5 rounded-lg text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleCreateTeam}
                    disabled={!newTeamName.trim() || teamMembers.length === 0}
                    className="btn-primary px-8 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
                >
                    <Plus size={16} className="mr-2" />
                    Create Team
                </button>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // REPLICA LIST
    // ═══════════════════════════════════════════════════════════════════════════
    const ReplicasList = () => (
        <div className="card overflow-hidden">
            {/* Search + Actions */}
            <div className="p-5 border-b border-hig-divider flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Briefcase size={20} className="text-hig-accent" />
                    <h2 className="text-base font-bold text-hig-text-primary">Hired Replicas</h2>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search replicas..."
                            className="w-full sm:w-56 pl-9 pr-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                        />
                    </div>
                    <button
                        onClick={() => onNavigate('marketplace')}
                        className="btn-primary text-sm px-4 py-2 rounded-lg whitespace-nowrap"
                    >
                        <Plus size={14} className="mr-1.5" />
                        Hire Replica
                    </button>
                </div>
            </div>

            {/* List */}
            {filteredReplicas.length === 0 ? (
                <div className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                        <Briefcase size={28} className="text-hig-text-tertiary" />
                    </div>
                    {hiredReplicas.length === 0 ? (
                        <>
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Replicas Hired Yet</h3>
                            <p className="text-sm text-hig-text-secondary mb-6 max-w-sm mx-auto">
                                Browse the marketplace to hire human-owned Replicas for your company operations.
                            </p>
                            <button
                                onClick={() => onNavigate('marketplace')}
                                className="btn-primary"
                            >
                                <Plus size={16} className="mr-2" />
                                Browse Marketplace
                            </button>
                        </>
                    ) : (
                        <>
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Results</h3>
                            <p className="text-sm text-hig-text-secondary">No replicas match "{searchQuery}"</p>
                        </>
                    )}
                </div>
            ) : (
                <div className="divide-y divide-hig-divider">
                    {filteredReplicas.map((replica) => (
                        <div
                            key={replica.id}
                            className="p-5 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md shadow-blue-500/15">
                                    {replica.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="font-semibold text-hig-text-primary">{replica.name}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${replica.status === 'deployed'
                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                            }`}>
                                            {replica.status === 'deployed' ? 'Active' : 'Draft'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-hig-text-secondary">
                                        <span className="flex items-center gap-1"><Tag size={11} /> {replica.category}</span>
                                        <span className="text-hig-text-tertiary">•</span>
                                        <span className="flex items-center gap-1">
                                            <User size={11} />
                                            Owner: {replica.creatorId.slice(0, 8)}...
                                        </span>
                                        <span className="text-hig-text-tertiary">•</span>
                                        <span className="flex items-center gap-1">
                                            <Shield size={11} className="text-emerald-400" />
                                            Guardian Active
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => onNavigate(`chat-${replica.id}`)}
                                    className="p-2 text-hig-text-tertiary hover:text-hig-accent hover:bg-hig-accent/10 rounded-lg transition-colors"
                                    title="Chat"
                                >
                                    <MessageSquare size={16} />
                                </button>
                                <button
                                    onClick={() => onNavigate(`tools-${replica.id}`)}
                                    className="p-2 text-hig-text-tertiary hover:text-orange-400 hover:bg-orange-400/10 rounded-lg transition-colors"
                                    title="Tools"
                                >
                                    <Settings size={16} />
                                </button>
                                <button
                                    onClick={() => onNavigate(`team-${replica.id}`)}
                                    className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-lg transition-colors"
                                    title="View Details"
                                >
                                    <Eye size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // EXISTING TEAMS LIST
    // ═══════════════════════════════════════════════════════════════════════════
    const TeamsList = () => (
        <div className="card overflow-hidden">
            <div className="p-5 border-b border-hig-divider flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Users size={20} className="text-indigo-400" />
                    <h2 className="text-base font-bold text-hig-text-primary">Teams</h2>
                </div>
                <button
                    onClick={() => setShowTeamBuilder(true)}
                    className="btn-primary text-sm px-4 py-2 rounded-lg"
                >
                    <Plus size={14} className="mr-1.5" />
                    New Team
                </button>
            </div>

            {teams.length === 0 ? (
                <div className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                        <Users size={28} className="text-hig-text-tertiary" />
                    </div>
                    <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Teams Yet</h3>
                    <p className="text-sm text-hig-text-secondary mb-6 max-w-sm mx-auto">
                        Create teams to organize replicas and human members for collaborative work.
                    </p>
                    <button
                        onClick={() => setShowTeamBuilder(true)}
                        className="btn-primary"
                    >
                        <Plus size={16} className="mr-2" />
                        Create First Team
                    </button>
                </div>
            ) : (
                <div className="divide-y divide-hig-divider">
                    {teams.map((team) => (
                        <div
                            key={team.id}
                            className="p-5 hover:bg-hig-bg-elevated/30 transition-colors cursor-pointer"
                            onClick={() => onNavigate(`team-${team.id}`)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                                        {team.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="font-semibold text-hig-text-primary">{team.name}</p>
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium flex items-center gap-1">
                                                <Shield size={10} />
                                                Guardian Active
                                            </span>
                                        </div>
                                        <p className="text-xs text-hig-text-secondary">
                                            {team._count?.members || 0} members • {team._count?.tasks || 0} tasks
                                        </p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-hig-text-tertiary" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="max-w-6xl mx-auto pb-12">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-hig-text-primary tracking-tight mb-2">
                        Replica Management
                    </h1>
                    <p className="text-hig-text-secondary">
                        Manage hired replicas and organize teams for {currentWorkspace?.name || 'your company'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowTeamBuilder(true)}
                        className="btn-secondary px-5 py-3 rounded-xl font-semibold text-sm"
                    >
                        <Users size={16} className="mr-2" />
                        Create Team
                    </button>
                    <button
                        onClick={() => onNavigate('marketplace')}
                        className="btn-primary px-5 py-3 rounded-xl font-semibold text-sm"
                    >
                        <Plus size={16} className="mr-2" />
                        Hire Replica
                    </button>
                </div>
            </div>

            {/* Stats */}
            <StatsBar />

            {/* Team Builder (conditional) */}
            {showTeamBuilder && <TeamBuilderPanel />}

            {/* Tab Switch */}
            <div className="flex items-center gap-1 p-1 bg-hig-bg-tertiary/50 rounded-xl mb-6 w-fit">
                <button
                    onClick={() => setSelectedTab('replicas')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${selectedTab === 'replicas'
                        ? 'bg-hig-bg-secondary text-hig-text-primary shadow-sm'
                        : 'text-hig-text-secondary hover:text-hig-text-primary'
                        }`}
                >
                    <Briefcase size={14} className="inline mr-1.5 -mt-0.5" />
                    Replicas ({hiredReplicas.length})
                </button>
                <button
                    onClick={() => setSelectedTab('teams')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${selectedTab === 'teams'
                        ? 'bg-hig-bg-secondary text-hig-text-primary shadow-sm'
                        : 'text-hig-text-secondary hover:text-hig-text-primary'
                        }`}
                >
                    <Users size={14} className="inline mr-1.5 -mt-0.5" />
                    Teams ({teams.length})
                </button>
            </div>

            {/* Content */}
            {selectedTab === 'replicas' ? <ReplicasList /> : <TeamsList />}

            {/* Ownership Notice */}
            <div className="mt-8 rounded-2xl bg-gradient-to-r from-hig-bg-secondary via-hig-bg-tertiary/50 to-hig-bg-secondary border border-hig-divider p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-indigo-400" />
                </div>
                <div>
                    <p className="font-semibold text-hig-text-primary text-sm mb-0.5">Replicas remain human-owned</p>
                    <p className="text-xs text-hig-text-secondary leading-relaxed">
                        Your company hires Replicas—it doesn't own them. Each Replica remains under the full sovereignty of its human owner,
                        who can suspend or terminate it at any time.
                    </p>
                </div>
            </div>
        </div>
    );
}
